import createTxpDataRow from './createTxpDataRow';
import populateFlightBusStopData from './populateFlightBusStopData';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import FlightCardItem from '../../schema/viewSchema/FlightCardItem';
import FlightReservation from '../../schema/FlightReservation';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpStrings from '../../schema/configSchema/TxpStrings';
import TxpDateTimeCallbacks from 'txp-data/lib/schema/configSchema/TxpDateTimeCallbacks';
import differenceInSeconds from 'date-fns/difference_in_seconds';
import isAfter from 'date-fns/is_after';

// Extracts the flight specific data needed for displaying the card
export default function createFlightCard(
    flightEntity: FlightReservation,
    isBusStop: boolean,
    activityId: string,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons,
    txpDateTimeCallbacks?: TxpDateTimeCallbacks
): FlightCardItem {
    let seat: string;
    let isFutureFlight: boolean = false;

    let summaryFirstLine = txpStrings.formatters.format(
        txpStrings.strings.flightSummaryFirstLine,
        flightEntity.arrivalAirport.city
    );
    let summarySecondLine = txpStrings.formatters.format(
        txpStrings.strings.flightSummarySecondLine,
        flightEntity.airline.name,
        flightEntity.airline.iataCode,
        flightEntity.flightNumber
    );
    let summaryThirdLine =
        flightEntity.reservationId &&
        txpStrings.formatters.format(
            txpStrings.strings.flightSummaryThirdLine,
            flightEntity.reservationId
        );
    let summaryFourthLine: string;
    let departureTime: string = flightEntity.departureTime;
    let arrivalTime: string = flightEntity.arrivalTime;
    let busStopData: BusStopData;

    let departureIATA = flightEntity.departureAirport.iataCode;
    let departsCity = flightEntity.departureAirport.city;

    let departureTimeDisplayText = txpStrings.formatters.formatUserTime(departureTime);
    let departureDateAriaText = txpStrings.formatters.formatWeekDayMonthDayYear(departureTime);
    let departureDateDisplayText = txpStrings.formatters.formatWeekDayDate(departureTime);
    let departureTerminal = flightEntity.departureTerminal;
    let departureGate = flightEntity.departureGate;

    let arrivalIATA = flightEntity.arrivalAirport.iataCode;
    let arrivalCity = flightEntity.arrivalAirport.city;
    let arrivalTimeDisplayText = txpStrings.formatters.formatUserTime(arrivalTime);
    let arrivalDateAriaText = txpStrings.formatters.formatWeekDayMonthDayYear(arrivalTime);
    let arrivalDateDisplayText = txpStrings.formatters.formatWeekDayDate(arrivalTime);
    let arrivalTerminal = flightEntity.arrivalTerminal;
    let arrivalGate = flightEntity.arrivalGate;
    summaryFourthLine = txpStrings.formatters.formatWeekDayDateTime(departureTime);

    /** Brands properties */
    let brandsSummarySecondLine = txpStrings.formatters.format(
        txpStrings.strings.brandSummaryFlightSecondLineText,
        flightEntity.airline.iataCode,
        flightEntity.flightNumber,
        flightEntity.arrivalAirport.iataCode,
        txpStrings.formatters.formatShortMonthDayYear(departureTime)
    );

    let ariaText: string = txpStrings.formatters.format(
        txpStrings.strings.flightDepartureArrivalAriaText,
        departureTimeDisplayText,
        departureDateAriaText,
        departsCity,
        departureIATA,
        arrivalTimeDisplayText,
        arrivalDateAriaText,
        arrivalCity,
        arrivalIATA
    );

    if (txpDateTimeCallbacks) {
        isFutureFlight = txpDateTimeCallbacks.isFutureTime(flightEntity.departureTimeUTC);
    } else {
        let departureTimeUTC = new Date(flightEntity.departureTimeUTC);
        let currentTime = new Date();
        if (isAfter(departureTimeUTC, currentTime)) {
            isFutureFlight = true;
        }
    }

    const actionButtonData =
        flightEntity.checkinUrl && isFutureFlight
            ? {
                  actionIconName: detailsIcons.airPlane,
                  actionUrl: flightEntity.checkinUrl,
                  actionButtonText: txpStrings.strings.checkinText,
                  actionName: ActionNameConstants.CheckIn,
              }
            : null;

    if (flightEntity.arrivalTimeUTC && flightEntity.departureTimeUTC) {
        const arrivalTimeUTC = new Date(flightEntity.arrivalTimeUTC);
        const departureTimeUTC = new Date(flightEntity.departureTimeUTC);
        const duration: number = differenceInSeconds(arrivalTimeUTC, departureTimeUTC);

        let durationTime: string = txpStrings.formatters.durationFormatter(duration);
        summaryFourthLine = txpStrings.formatters.format(
            txpStrings.strings.flightSummaryFourthLine,
            summaryFourthLine,
            durationTime
        );
    }

    if (
        flightEntity.reservedTicket &&
        flightEntity.reservedTicket.seat &&
        (flightEntity.reservedTicket.seat.seatNumber || flightEntity.reservedTicket.seat.row)
    ) {
        seat = txpStrings.formatters.format(
            txpStrings.strings.flightSeatText,
            flightEntity.reservedTicket.seat.seatNumber,
            flightEntity.reservedTicket.seat.row
        );
    }

    if (isBusStop) {
        busStopData = populateFlightBusStopData(flightEntity, txpStrings, detailsIcons.airPlane);
    }

    return {
        departureIATA: departureIATA,
        arrivalIATA: arrivalIATA,
        departureTime: departureTimeDisplayText,
        arrivalTime: arrivalTimeDisplayText,
        departureDate: departureDateDisplayText,
        arrivalDate: arrivalDateDisplayText,
        departureTerminal: createTxpDataRow(
            departureTerminal,
            txpStrings.strings.flightTerminalLabel,
            null
        ),
        departureGate: createTxpDataRow(departureGate, txpStrings.strings.flightGateLabel, null),
        arrivalTerminal: createTxpDataRow(
            arrivalTerminal,
            txpStrings.strings.flightTerminalLabel,
            null
        ),
        arrivalGate: createTxpDataRow(arrivalGate, txpStrings.strings.flightGateLabel, null),
        arrivalsIcon: detailsIcons.arrivals,
        departureCity: departsCity,
        arrivalCity: arrivalCity,
        seat: createTxpDataRow(seat, txpStrings.strings.flightSeatLabel, null),
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
            thirdLine: { text: summaryThirdLine },
            fourthLine: createTxpDataRow(
                summaryFourthLine,
                txpStrings.strings.flightDepartureLabel,
                detailsIcons.clock
            ),
        },
        ariaText: ariaText,
        entityIcon: entityIcon,
        entityImage: entityImage,
        activityType: ActivityType.FlightDeparture,
        brandsSummaryFirstLine: summaryFirstLine,
        brandsSummarySecondLine: brandsSummarySecondLine,
        busStopData: busStopData,
        id: activityId,
        entityType: EntityType.FlightReservation,
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        isFutureFlight: isFutureFlight,
    } as FlightCardItem;
}
